import { useTranslation } from "@refinedev/core";

import { Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";

import { CardButton } from "@/components/common";
import { maskPhone } from "@/utils/phone-utils";

export interface MfaEmailSetupProps {
  /** Whether the user's phone is already verified */
  phoneVerified: boolean;
  /** The user's current phone number (if any) */
  existingPhoneNumber: string | null;
  /** Called when email MFA should be enabled */
  onEnable: () => void;
  /** Whether submission is in progress */
  isSubmitting: boolean;
  /** Error message to display */
  errorMessage: string;
  /** Whether to show the set-as-preferred option */
  showSetPreferred?: boolean;
  /** Whether to set as preferred */
  setAsPreferred?: boolean;
  /** Callback for preferred change */
  onSetPreferredChange?: (value: boolean) => void;
  /** Custom submit button label */
  submitButtonLabel?: string;
  /**
   * Whether the user has acknowledged the no-phone warning.
   * Required when phoneVerified is false to show the enable button.
   */
  acknowledgedNoPhone?: boolean;
}

/**
 * Email MFA setup component.
 * checkbox is shown in the card's extraContent when no phone is on file.
 * Recovery info is also shown in the card's extraContent.
 * This component only renders the enable button and error message.
 */
export const MfaEmailSetup: React.FC<MfaEmailSetupProps> = ({
  phoneVerified,
  existingPhoneNumber,
  onEnable,
  isSubmitting,
  errorMessage,
  showSetPreferred = false,
  setAsPreferred = false,
  onSetPreferredChange,
  submitButtonLabel,
  acknowledgedNoPhone = false,
}) => {
  const { translate } = useTranslation();

  // Can proceed if phone is verified OR user acknowledged the warning
  const canProceed = phoneVerified || acknowledgedNoPhone;

  if (!canProceed) {
    return null;
  }

  return (
    <Stack spacing={2} mt={1.25}>
      {/* Set as Preferred Question (Profile only) */}
      {showSetPreferred && (
        <Typography variant="caption" color="primary">
          {translate("pages.profile.mfa.askSetPreferred", { method: translate("pages.profile.mfa.emailTitle") })}
        </Typography>
      )}

      {/* SMS recovery info - shown when phone is verified */}
      {showSetPreferred && phoneVerified && existingPhoneNumber && (
        <Typography variant="caption" color="primary">
          {translate("pages.profile.mfa.smsRecoveryInfo", { phoneNumber: maskPhone(existingPhoneNumber) })}
        </Typography>
      )}

      {/* Set as Preferred Checkbox (Profile only) */}
      {showSetPreferred && (
        <FormControlLabel
          control={
            <Checkbox
              checked={setAsPreferred}
              onChange={e => onSetPreferredChange?.(e.target.checked)}
              size="small"
              disabled={isSubmitting}
            />
          }
          label={<Typography variant="body1">{translate("pages.mfa.setEmailPreferred")}</Typography>}
        />
      )}

      {/* Error Message */}
      {errorMessage && (
        <Typography variant="body2" color="error.dark" align="center">
          {errorMessage}
        </Typography>
      )}

      {/* Enable Button */}
      <CardButton onClick={onEnable} disabled={isSubmitting}>
        {isSubmitting
          ? translate("pages.profile.mfa.enablingButton")
          : (submitButtonLabel ?? translate("pages.profile.mfa.enableButton"))}
      </CardButton>
    </Stack>
  );
};
